import clsx from "clsx";
import React from "react";
import {
  Checkbox,
  DatePicker,
  FileField,
  Input,
  RadioGroup,
  Switch,
  TextArea,
} from "@pd/pd-ui-kit";
import type {
  CheckboxProps,
  DatePickerProps,
  FileFieldProps,
  InputProps,
  SelectProps,
  SwitchProps,
  TextAreaProps,
} from "@pd/pd-ui-kit";
import type { RadioGroupProps } from "antd";
import { Controller } from "react-hook-form";
import type { Control, FieldValues, RegisterOptions } from "react-hook-form";

import { Select } from "~/shared/ui/Select";

import s from "./Field.less";

const COMPONENTS = {
  input: Input,
  textarea: TextArea,
  select: Select,
  checkbox: Checkbox,
  switch: Switch,
  datePicker: DatePicker,
  radio: RadioGroup,
  file: FileField,
} as const;

export type FieldType = keyof typeof COMPONENTS;

/**
 * Пропсы, которыми управляет сам Field (через react-hook-form и props error/label).
 * Их нельзя прокинуть снаружи — иначе возможен конфликт с Controller.
 */
type ControlledKeys =
  | "value"
  | "checked"
  | "onChange"
  | "status"
  | "errorText"
  | "defaultValue";

interface FieldOwnProps<TFieldValues extends FieldValues = FieldValues> {
  /** Имя поля в форме (как в Controller). */
  name: string;
  /** control из useForm. */
  control: Control<TFieldValues>;
  /** Подпись поля. Для type="checkbox" передаётся в нативный label чекбокса pd-ui-kit. */
  label?: React.ReactNode;
  /** Текст ошибки (обычно errors[name]?.message из useForm). */
  error?: string;
  /** Правила валидации react-hook-form. */
  rules?: Exclude<
    RegisterOptions,
    "valueAsNumber" | "valueAsDate" | "setValueAs"
  >;
  /** defaultValue для Controller (если не задано в useForm defaultValues). */
  defaultValue?: unknown;
  /** Дополнительный класс обёртки поля. */
  wrapperClassName?: string;
  /**
   * Колбэк, вызываемый вместе с записью значения в форму.
   * Получает уже нормализованное значение (для checkbox/switch — boolean).
   */
  onChange?: (value: unknown) => void;
  /**
   * Колбэк, вызываемый вместе с onBlur контроллера (side-effect,
   * например сброс поисковой строки селекта).
   */
  onBlur?: () => void;
}

export type FieldProps<TFieldValues extends FieldValues = FieldValues> =
  FieldOwnProps<TFieldValues> &
    (
      | ({ type: "input" } & Omit<InputProps, ControlledKeys | "type"> & {
            /** HTML-тип инпута (number, password, …): antd InputProps["type"]
             * конфликтует с дискриминантом Field, поэтому пробрасывается так. */
            inputType?: InputProps["type"];
          })
      | ({ type: "textarea" } & Omit<TextAreaProps, ControlledKeys>)
      | ({ type: "select" } & Omit<SelectProps, ControlledKeys>)
      | ({ type: "checkbox" } & Omit<CheckboxProps, ControlledKeys | "label">)
      | ({ type: "switch" } & Omit<SwitchProps, ControlledKeys>)
      | ({ type: "datePicker" } & Omit<DatePickerProps, ControlledKeys>)
      | ({ type: "radio" } & Omit<RadioGroupProps, ControlledKeys>)
      | ({ type: "file" } & Omit<
          FileFieldProps,
          ControlledKeys | "label" | "error"
        >)
    );

const isCheckedType = (type: FieldType): boolean =>
  type === "checkbox" || type === "switch";

/**
 * Универсальное поле формы: оборачивает компонент из @pd/pd-ui-kit
 * в Controller от react-hook-form и рисует label + ошибку.
 *
 * Набор доступных пропсов зависит от `type` — см. README.md рядом.
 */
export const Field = <TFieldValues extends FieldValues = FieldValues>(
  props: FieldProps<TFieldValues>,
) => {
  const {
    type,
    name,
    control,
    label,
    error,
    rules,
    defaultValue,
    wrapperClassName,
    onChange: onChangeProp,
    onBlur: onBlurProp,
    ...rest
  } = props;

  const { inputType, ...passthrough } = rest as Record<string, unknown> & {
    inputType?: string;
  };

  // Объединённый тип всех компонентов кита слишком сложен для JSX-инференса,
  // поэтому внутри работаем через общий тип; внешняя типизация — через FieldProps.
  const Component = COMPONENTS[type] as React.ComponentType<
    Record<string, unknown>
  >;

  return (
    <Controller
      control={control}
      name={name}
      rules={rules}
      defaultValue={defaultValue}
      render={({ value, onChange, onBlur, ref }) => {
        const handleChange = (nextValue: unknown) => {
          onChange(nextValue);
          onChangeProp?.(nextValue);
        };

        const componentProps: Record<string, unknown> = {
          ...passthrough,
          ...(type === "input" && inputType ? { type: inputType } : {}),
          // У текстовых полей undefined превращаем в "", чтобы не ловить
          // uncontrolled -> controlled warning; select/datePicker сами
          // корректно обрабатывают undefined (показывают placeholder).
          ...(isCheckedType(type)
            ? { checked: Boolean(value) }
            : type === "input" || type === "textarea"
              ? { value: value ?? "" }
              : { value }),
          onChange:
            type === "checkbox"
              ? (e: { target?: { checked?: boolean } }) =>
                  handleChange(Boolean(e?.target?.checked))
              : type === "switch"
                ? (checked: boolean) => handleChange(checked)
                : type === "radio"
                  ? (e: { target?: { value?: unknown } }) =>
                      handleChange(e?.target?.value)
                  : handleChange,
          onBlur: () => {
            onBlur();
            onBlurProp?.();
          },
          // FileField — обычный function component без forwardRef,
          // поэтому ref ему не передаём (иначе warning React).
          ...(type !== "file" ? { ref } : {}),
          // antd Radio.Group пробрасывает неизвестные пропсы в DOM,
          // поэтому status ему не передаём — ошибка видна по тексту под полем.
          // FileField сам рендерит ошибку из пропса error.
          status:
            error && type !== "radio" && type !== "file" ? "error" : undefined,
          style: {
            width: "100%",
            ...(passthrough as { style?: object }).style,
          },
          // У чекбокса подпись по дизайну стоит рядом с боксом —
          // отдаём её в нативный label компонента кита.
          ...(type === "checkbox" ? { label } : {}),
          // FileField сам рисует label и ошибку, а значение меняет через
          // onUpload (вернул FileFieldFile — пишем в форму) и onDelete (null).
          ...(type === "file"
            ? {
                value: value ?? null,
                label,
                error,
                onUpload: async (file: File) => {
                  const uploaded = await (
                    passthrough as FileFieldProps
                  ).onUpload?.(file);
                  if (uploaded) handleChange(uploaded);
                },
                onDelete: () => {
                  handleChange(null);
                  (passthrough as FileFieldProps).onDelete?.();
                },
              }
            : {}),
        };

        return (
          <div className={clsx(s.field, wrapperClassName)}>
            {label && type !== "checkbox" && type !== "file" && (
              <span className={s.fieldLabel}>{label}</span>
            )}
            <Component {...componentProps} />
            {error && type !== "file" && (
              <span className={s.fieldError}>{error}</span>
            )}
          </div>
        );
      }}
    />
  );
};

export default Field;
